/**
 * Curated endpoint constants. Single source of truth for paths the React app
 * consumes. Authoritative spec: ../../../../docs/api/yaml.yml.
 *
 * Phase 1 only ships /api/me — additional endpoints land alongside each
 * feature migration (phase 3.x).
 */

export const ENDPOINTS = {
  me: "/api/me",
  bootstrap: "/api/bootstrap",
  posts: "/api/posts",
  post: (postId: string) => `/api/posts/${encodeURIComponent(postId)}`,
  postLike: (postId: string) => `/api/posts/${encodeURIComponent(postId)}/like`,
  postReactions: (postId: string) => `/api/posts/${encodeURIComponent(postId)}/reactions`,
  postComments: (postId: string) => `/api/posts/${encodeURIComponent(postId)}/comments`,
  postComment: (postId: string, commentId: string) =>
    `/api/posts/${encodeURIComponent(postId)}/comments/${encodeURIComponent(commentId)}`,
  dailyTopic: "/api/daily-topic",
  dailyTopicRespond: "/api/daily-topic/respond",
  emotionsToday: "/api/emotions/today",
  islands: "/api/islands",
  driftBottles: "/api/drift-bottles",
  driftBottlePick: "/api/drift-bottles/pick",
  driftBottleReplies: (bottleId: string) =>
    `/api/drift-bottles/${encodeURIComponent(bottleId)}/replies`,
  driftBottleThank: (bottleId: string) =>
    `/api/drift-bottles/${encodeURIComponent(bottleId)}/thank`,
  boardFollow: (boardId: string) => `/api/boards/${encodeURIComponent(boardId)}/follow`,
  boardPin: (boardId: string, postId: string) =>
    `/api/boards/${encodeURIComponent(boardId)}/pins/${encodeURIComponent(postId)}`,
  authEmailCode: "/api/auth/email-code",
  authRegister: "/api/auth/register",
  authLogin: "/api/auth/login",
  authChangePassword: "/api/auth/change-password",
  bookmarks: "/api/bookmarks",
  bookmark: (postId: string) => `/api/bookmarks/${encodeURIComponent(postId)}`,
  reports: "/api/reports",
  notifications: (limit = 30) => `/api/notifications?limit=${encodeURIComponent(String(limit))}`,
  notificationRead: (id: string) => `/api/notifications/${encodeURIComponent(id)}/read`,
  notificationsReadAll: "/api/notifications/read-all",
  search: (query: string, type: "post" | "board" | "user") =>
    `/api/search?q=${encodeURIComponent(query)}&type=${encodeURIComponent(type)}`,
  proposalsRoot: "/api/proposals",
  proposals: (status: string) => `/api/proposals?status=${encodeURIComponent(status)}`,
  proposalSupport: (proposalId: string) =>
    `/api/proposals/${encodeURIComponent(proposalId)}/support`,
  proposalReview: (proposalId: string) => `/api/proposals/${encodeURIComponent(proposalId)}/review`,
  user: (userId: string) => `/api/users/${encodeURIComponent(userId)}`,
  userPosts: (userId: string) => `/api/users/${encodeURIComponent(userId)}/posts`,
  userFollowers: (userId: string) => `/api/users/${encodeURIComponent(userId)}/followers`,
  userFollowing: (userId: string) => `/api/users/${encodeURIComponent(userId)}/following`,
  userFollow: (userId: string) => `/api/users/${encodeURIComponent(userId)}/follow`,
  /**
   * Planned: tree-hole-api/docs/plans/profile-violations-v1.md (status=proposed).
   * Until shipped, `fetchUserViolations()` falls back to a deterministic mock so
   * the H5 .profile-stickers#profileViolations section can land in the UI.
   */
  userViolations: (userId: string) =>
    `/api/users/${encodeURIComponent(userId)}/violations`,
  uploads: "/api/uploads",
} as const;

export type EndpointKey = keyof typeof ENDPOINTS;
